import type { CambridgeLevel, CambridgePart, CambridgeGenre } from "./criteria";

export interface Stimulus {
  id: string;
  level: CambridgeLevel;
  part: CambridgePart;
  genre: CambridgeGenre | null; // null for Part 1 (always an essay)
  promptText: string; // the human task instruction shown to the learner (includes the word range)
  inputPoints?: string[]; // the essay's two ideas / the task's content (bullet) points
}

const STIMULI: Stimulus[] = [
  // ===========================================================================
  // PART 1 — discursive essay (every level)
  // ===========================================================================
  {
    id: "b2-p1-screens",
    level: "b2",
    part: "part1",
    genre: null,
    promptText:
      "In your English class you have been talking about how people use technology. Now your teacher has asked you to write an essay. Write an essay using all the notes and giving reasons for your point of view. Write 140–190 words.\n\nEssay question: Some people think children spend too much time looking at screens. Do you agree?",
    inputPoints: ["schoolwork and learning", "free time and friendships", "(your own idea)"],
  },
  {
    id: "b2-p1-environment",
    level: "b2",
    part: "part1",
    genre: null,
    promptText:
      "In your English class you have been talking about protecting the environment. Now your teacher has asked you to write an essay. Write an essay using all the notes and giving reasons for your point of view. Write 140–190 words.\n\nEssay question: What is the best way for ordinary people to help the environment?",
    inputPoints: ["transport choices", "shopping habits", "(your own idea)"],
  },
  {
    id: "c1-p1-work",
    level: "c1",
    part: "part1",
    genre: null,
    promptText:
      "Your class has attended a panel discussion on the future of work. You have made the notes below. Write an essay discussing two of the points in your notes. You should explain which way of working you think is more beneficial, giving reasons to support your opinion. You may, if you wish, make use of the opinions expressed in the discussion, but you should use your own words as far as possible. Write 220–260 words.\n\nWays of working in the future: working from home, the four-day week, working for several employers at once.",
    inputPoints: [
      "working from home",
      "the four-day week",
      "working for several employers at once",
    ],
  },
  {
    id: "c1-p1-culture",
    level: "c1",
    part: "part1",
    genre: null,
    promptText:
      "Your class has listened to a radio programme about preserving local culture. You have made the notes below. Write an essay discussing two of the ways below of keeping local culture alive. You should explain which way you think is more effective, giving reasons to support your opinion. Write 220–260 words.\n\nWays of keeping local culture alive: museums and festivals, teaching it in schools, sharing it online.",
    inputPoints: ["museums and festivals", "teaching it in schools", "sharing it online"],
  },
  {
    id: "c2-p1-progress",
    level: "c2",
    part: "part1",
    genre: null,
    promptText:
      "Read the two texts below about whether technological progress always improves people's lives. Write an essay summarising and evaluating the key points from both texts. Use your own words throughout as far as possible, and include your own ideas in your answer. Write 240–280 words.\n\nText A argues that each new technology frees people from drudgery and connects them more widely than before. Text B counters that convenience often comes at the cost of attention, privacy and genuine human contact.",
    inputPoints: [
      "the benefits each text claims",
      "the costs or risks each text raises",
      "your own evaluation of which case is stronger",
    ],
  },

  // ===========================================================================
  // PART 2 — B2 First (situational tasks)
  // ===========================================================================
  {
    id: "b2-p2-article-hero",
    level: "b2",
    part: "part2",
    genre: "article",
    promptText:
      "You see this announcement in an international English-language magazine. Write your article in 140–190 words.\n\n'Who is your hero? Tell us about a person you really admire — they can be someone famous or someone you know. What have they done, and why do they inspire you? We will publish the most interesting articles next month.'",
    inputPoints: [
      "introduce the person and how you know of them",
      "describe what they have done",
      "explain why they inspire you",
    ],
  },
  {
    id: "b2-p2-email-friend",
    level: "b2",
    part: "part2",
    genre: "letter",
    promptText:
      "You have received an email from your English-speaking friend Alex, who is coming to stay with you for a week. Write your email in reply in 140–190 words.\n\n'I can't wait to visit! What should I pack — what's the weather like? Is there anything special you'd like us to do while I'm there? And shall I bring anything from home?'",
    inputPoints: [
      "tell Alex what to pack and describe the weather",
      "suggest something special to do together",
      "answer the question about bringing something from home",
    ],
  },
  {
    id: "b2-p2-review-app",
    level: "b2",
    part: "part2",
    genre: "review",
    promptText:
      "You see this notice on an English-language website. Write your review in 140–190 words.\n\n'Reviews wanted! Tell us about a mobile app you use a lot. What does it do, what do you like about it, and would you recommend it to other readers? The best reviews will appear on our homepage.'",
    inputPoints: [
      "say what the app is and what it does",
      "explain what you like (and dislike) about it",
      "say whether you recommend it and to whom",
    ],
  },
  {
    id: "b2-p2-report-club",
    level: "b2",
    part: "part2",
    genre: "report",
    promptText:
      "Your college principal has asked you to write a report about the college sports club. Write your report in 140–190 words.\n\nIn your report you should describe what the club offers at the moment, explain what students think of it, and suggest one improvement.",
    inputPoints: [
      "describe what the sports club currently offers",
      "explain what students think of it",
      "recommend one improvement",
    ],
  },

  // ===========================================================================
  // PART 2 — C1 Advanced (situational tasks)
  // ===========================================================================
  {
    id: "c1-p2-letter-application",
    level: "c1",
    part: "part2",
    genre: "letter",
    promptText:
      "You have seen the following advertisement for a summer role at an international youth camp and decide to apply. Write your letter of application in 220–260 words.\n\n'Activity leaders wanted! We need enthusiastic, responsible people to run sports and creative workshops for teenagers from around the world. Tell us about your relevant experience, the skills you would bring, and why you want to work with us.'",
    inputPoints: [
      "state the role you are applying for and how you heard about it",
      "describe your relevant experience and skills",
      "explain why you want this particular role",
    ],
  },
  {
    id: "c1-p2-review-restaurant",
    level: "c1",
    part: "part2",
    genre: "review",
    promptText:
      "An international food website has invited readers to submit reviews. Write your review in 220–260 words.\n\n'Best meal out: Tell us about a restaurant or café that impressed you. Describe the atmosphere, the food and the service, and explain whether it lived up to its reputation. Would you go back?'",
    inputPoints: [
      "describe the place, its atmosphere and the food",
      "comment on the service and whether it met your expectations",
      "give a clear recommendation and verdict",
    ],
  },
  {
    id: "c1-p2-report-facilities",
    level: "c1",
    part: "part2",
    genre: "report",
    promptText:
      "The director of the international company where you are studying has asked you to write a report. Write your report in 220–260 words.\n\nYour report should assess the current facilities for staff (the canteen, the break areas and the gym), explain how well they are used, and recommend where money would be best spent to improve them.",
    inputPoints: [
      "assess the current staff facilities",
      "explain how well each is used",
      "recommend where to invest for the biggest benefit",
    ],
  },
  {
    id: "c1-p2-article-travel",
    level: "c1",
    part: "part2",
    genre: "article",
    promptText:
      "You see this announcement in an international travel magazine. Write your article in 220–260 words.\n\n'Has travel changed you? We want articles about a journey that changed the way you see the world. Where did you go, what happened, and how are you different now? The best articles will be featured in next month's issue.'",
    inputPoints: [
      "set the scene: where you went and why",
      "describe what happened that affected you",
      "reflect on how the experience changed you",
    ],
  },

  // ===========================================================================
  // PART 2 — C2 Proficiency (situational tasks)
  // ===========================================================================
  {
    id: "c2-p2-article-reading",
    level: "c2",
    part: "part2",
    genre: "article",
    promptText:
      "An international current-affairs magazine has invited articles on the following topic. Write your article in 240–280 words.\n\n'In an age of endless screens, is reading for pleasure dying out — or simply changing shape? Make a case for how reading habits are evolving and what, if anything, is lost or gained.'",
    inputPoints: [
      "characterise how reading habits are changing",
      "weigh what is being lost against what is being gained",
      "advance and defend your own position engagingly",
    ],
  },
  {
    id: "c2-p2-review-novel",
    level: "c2",
    part: "part2",
    genre: "review",
    promptText:
      "A literary website has asked readers to review a book that divided opinion. Write your review in 240–280 words.\n\nDescribe the book and what makes it controversial, evaluate how successfully it achieves what it sets out to do, and explain who you would — and would not — recommend it to.",
    inputPoints: [
      "introduce the book and why it divides opinion",
      "evaluate how well it succeeds on its own terms",
      "give a nuanced, qualified recommendation",
    ],
  },
  {
    id: "c2-p2-letter-editor",
    level: "c2",
    part: "part2",
    genre: "letter",
    promptText:
      "You have read an editorial in an international newspaper arguing that universities should focus only on subjects with clear job prospects. You disagree. Write a letter to the editor in 240–280 words.\n\nIn your letter, challenge the editorial's central claim, set out the wider value you believe higher education offers, and propose a more balanced view.",
    inputPoints: [
      "challenge the editorial's central claim",
      "set out the wider value of higher education",
      "propose a balanced, persuasive alternative view",
    ],
  },
];

export const stimuliFor = (
  level: CambridgeLevel,
  part: CambridgePart,
  genre: CambridgeGenre | null,
): Stimulus[] =>
  STIMULI.filter(
    (s) => s.level === level && s.part === part && (part === "part1" || s.genre === genre),
  );

export const getStimulus = (
  level: CambridgeLevel,
  part: CambridgePart,
  genre: CambridgeGenre | null,
  id: string,
): Stimulus | undefined => stimuliFor(level, part, genre).find((s) => s.id === id);

// Render the marking ground-truth as text for the marker prompt: the task's title
// and the content/input points the response must cover. This is the GROUND TRUTH
// for the Content subscale — the model checks every point is addressed.
export const serialiseStimulusForMarking = (s: Stimulus): string => {
  const points =
    s.inputPoints && s.inputPoints.length > 0
      ? `\nThe response MUST cover all of these content points (GROUND TRUTH for the Content subscale):\n${s.inputPoints.map((p) => `  - ${p}`).join("\n")}`
      : "";
  return `TASK INPUT — "${s.promptText.split("\n")[0]}"${points}`;
};
